import dayjs from 'dayjs';
import { DayReport } from './tabData';
import { leftOutOf, DayLeftOut } from './briefingPolicy';

/**
 * 지난 리포트 분석.
 *
 * 히트맵은 색만 칠하면 "그래서 내가 잘하고 있는 건가"가 안 남는다.
 * 색이 진하다는 건 그날 잘했다는 뜻일 뿐, 이번 달이 지난달보다 나은지,
 * 어느 요일에 자꾸 놓치는지는 달력만 봐서는 알 수 없다.
 *
 * 그래서 네 가지를 계산한다.
 *   ① 월 요약 — 며칠 중 며칠 챙겼나, 지난달보다 나은가
 *   ② 주차별 — 이번 달 안에서도 흐름이 있다
 *   ③ 요일별 — 월요일마다 놓친다면 그건 바로 고칠 수 있는 문제다
 *   ④ 연속 기록 — 습관은 연속으로 만들어진다
 */

/** 브리핑이 올라온 날만 센다. 이슈가 없던 날을 미실행으로 세면 억울하다 */
export const isActiveDay = (d: DayReport) => d.items > 0;

export const executionRate = (d: DayReport) => (d.items ? d.acted / d.items : 1);

/** 그날의 운영 등급 — 히트맵 색과 요약이 같은 기준을 쓰게 한다 */
export type DayLevel = 'NONE' | 'PERFECT' | 'GOOD' | 'HALF' | 'POOR';

export const levelOf = (d: DayReport | undefined): DayLevel => {
    if (!d || d.items === 0) return 'NONE';
    const r = executionRate(d);
    if (r >= 1) return 'PERFECT';
    if (r >= 0.6) return 'GOOD';
    if (r >= 0.3) return 'HALF';
    return 'POOR';
};

export const LEVEL_META: Record<
    DayLevel,
    { label: string; bg: string; text: string; dot: string; border: string; muted: string }
> = {
    // 단계가 한눈에 갈리도록 대비를 세게 준다. 옅은 색끼리는 스쳐 보면 구분이 안 된다.
    PERFECT: { label: '전부 실행', bg: '#0B7C6C', text: '#FFFFFF', dot: '#0B7C6C', border: '#0B7C6C', muted: 'rgba(255,255,255,0.55)' },
    GOOD: { label: '대부분 실행', bg: '#41B39D', text: '#FFFFFF', dot: '#41B39D', border: '#41B39D', muted: 'rgba(255,255,255,0.55)' },
    HALF: { label: '절반 실행', bg: '#F8C978', text: '#6B4400', dot: '#F8C978', border: '#EFB855', muted: 'rgba(107,68,0,0.28)' },
    POOR: { label: '거의 못 함', bg: '#FBB3B8', text: '#9E1F29', dot: '#FBB3B8', border: '#F49AA1', muted: 'rgba(158,31,41,0.25)' },
    /*
     * 이슈가 0 건이던 날.
     *
     * 회색으로 두면 '브리핑이 만들어지지 않은 날'(빗금 회색)과 스쳐 보아 갈리지 않는다.
     * 그렇다고 경고색을 쓸 수도 없다 — 챙길 일이 없던 날은 결함이 아니라 성과다.
     * 그래서 아주 옅은 초록으로 둔다. 조용하지만 '아무것도 없는 칸'과는 다르게 보인다.
     */
    NONE: { label: '이슈 없던 날', bg: '#EFF7F3', text: '#7FA095', dot: '#DCEBE3', border: '#DCEBE3', muted: '#C9E0D6' },
};

/* ───────────────────────── 월 요약 ───────────────────────── */

export type MonthSummary = {
    monthKey: string;
    /** 브리핑이 올라온 날 수 */
    activeDays: number;
    /** 그중 전부 실행한 날 */
    perfectDays: number;
    /** 한 건도 실행 못 한 날 */
    missedDays: number;
    totalItems: number;
    totalActed: number;
    /**
     * 실행률 0~1.
     *
     * ⚠️ 올라온 항목이 한 건도 없으면 **null** 입니다. 0 이 아닙니다.
     * 0 으로 주면 화면에 "이번 달 실행률 0%"가 찍히는데, 그건
     * "챙길 일이 하나도 없었다"는 뜻이지 "다 놓쳤다"는 뜻이 아닙니다.
     * 원장님이 자기가 게을렀다고 읽을 숫자를 만들면 안 됩니다.
     */
    rate: number | null;
    /** 조치 결과 추적이 없는 기간은 null */
    defended: number | null;
    lost: number | null;
};

/**
 * 금액 합계.
 *
 * 아직 추적하지 않는 기간은 null 로 들어옵니다. 0 으로 보고 더하면
 * "지켜낸 매출 0원"이 화면에 뜨는데, 이건 못 지켰다는 뜻이 아니라
 * 아직 세지 않았다는 뜻입니다. 하나도 없으면 null 을 그대로 돌려줍니다.
 */
const sumMoney = (rows: DayReport[], key: 'defended' | 'lost'): number | null => {
    const known = rows.map((d) => d[key]).filter((v): v is number => typeof v === 'number');
    return known.length ? known.reduce((s, v) => s + v, 0) : null;
};

const monthKeyOf = (date: string) => date.slice(0, 7);

export const summarizeMonth = (reports: DayReport[], monthKey: string): MonthSummary => {
    const rows = reports.filter((d) => monthKeyOf(d.date) === monthKey);
    const active = rows.filter(isActiveDay);
    const totalItems = active.reduce((s, d) => s + d.items, 0);
    const totalActed = active.reduce((s, d) => s + d.acted, 0);

    return {
        monthKey,
        activeDays: active.length,
        perfectDays: active.filter((d) => levelOf(d) === 'PERFECT').length,
        missedDays: active.filter((d) => d.acted === 0).length,
        totalItems,
        totalActed,
        // 분모가 0이면 비율이 존재하지 않는다. 0 으로 채우지 않는다.
        rate: totalItems ? totalActed / totalItems : null,
        defended: sumMoney(rows, 'defended'),
        lost: sumMoney(rows, 'lost'),
    };
};

export type MonthComparison = {
    current: MonthSummary;
    previous: MonthSummary | null;
    /** 실행률 변화 (%p). 지난달 데이터가 없으면 null */
    ratePointDiff: number | null;
    verdict: 'BETTER' | 'SAME' | 'WORSE' | 'NO_BASE';
    headline: string;
};

/**
 * 지난달과 비교.
 * "61%"만 보면 잘한 건지 모른다. 지난달이 45%였는지 80%였는지가 전부다.
 */
export const compareMonth = (reports: DayReport[], monthKey: string): MonthComparison => {
    const current = summarizeMonth(reports, monthKey);
    const prevKey = dayjs(`${monthKey}-01`).subtract(1, 'month').format('YYYY-MM');
    const prevRows = reports.filter((d) => monthKeyOf(d.date) === prevKey);
    const previous = prevRows.length ? summarizeMonth(reports, prevKey) : null;

    /*
     * activeDays === 0 과 rate == null 은 같은 조건이다 —
     * 활성일은 items > 0 인 날만 세므로 활성일이 없으면 분모도 0이다.
     * rate 로 판정해야 아래 뺄셈에서 null 이 섞이지 않는 것이 타입으로 보장된다.
     */
    if (current.rate == null) {
        return {
            current,
            previous,
            ratePointDiff: null,
            verdict: 'NO_BASE',
            headline: '아직 브리핑 기록이 없는 달입니다',
        };
    }
    if (!previous || previous.rate == null) {
        return {
            current,
            previous,
            ratePointDiff: null,
            verdict: 'NO_BASE',
            headline: '비교할 지난달 기록이 아직 없습니다',
        };
    }

    const diff = Math.round((current.rate - previous.rate) * 1000) / 10;
    // 2%p 안쪽은 흔들림으로 본다. 매달 오르내린다고 알리면 신호가 죽는다.
    const verdict = diff >= 2 ? 'BETTER' : diff <= -2 ? 'WORSE' : 'SAME';
    const headline =
        verdict === 'BETTER'
            ? `지난달보다 ${Math.abs(diff)}%p 좋아졌습니다`
            : verdict === 'WORSE'
              ? `지난달보다 ${Math.abs(diff)}%p 떨어졌습니다`
              : '지난달과 비슷한 수준입니다';

    return { current, previous, ratePointDiff: diff, verdict, headline };
};

/* ───────────────────────── 주차별 ───────────────────────── */

export type WeekStat = {
    /** 그 주 일요일 */
    weekStart: string;
    items: number;
    acted: number;
    rate: number;
    defended: number | null;
    activeDays: number;
};

/** 달력 한 줄(주)마다 오른쪽에 붙일 요약 */
export const weeklyStats = (reports: DayReport[], monthKey: string): WeekStat[] => {
    const rows = reports.filter((d) => monthKeyOf(d.date) === monthKey);
    const buckets: Record<string, DayReport[]> = {};

    rows.forEach((d) => {
        const key = dayjs(d.date).startOf('week').format('YYYY-MM-DD');
        buckets[key] = [...(buckets[key] ?? []), d];
    });

    return Object.keys(buckets)
        .sort((a, b) => a.localeCompare(b))
        .map((weekStart) => {
            const list = buckets[weekStart];
            const active = list.filter(isActiveDay);
            const items = active.reduce((s, d) => s + d.items, 0);
            const acted = active.reduce((s, d) => s + d.acted, 0);
            return {
                weekStart,
                items,
                acted,
                rate: items ? acted / items : 0,
                defended: sumMoney(list, 'defended'),
                activeDays: active.length,
            };
        });
};

/* ───────────────────────── 요일별 ───────────────────────── */

export type WeekdayStat = {
    /** 0=일 ~ 6=토 */
    weekday: number;
    label: string;
    items: number;
    acted: number;
    rate: number;
    activeDays: number;
};

const WEEKDAY_LABEL = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 요일별 실행률.
 *
 * 이 화면에서 가장 실용적인 분석이다.
 * "화요일마다 못 챙긴다"를 알면 화요일 아침 알림을 켜는 것으로 바로 고칠 수 있다.
 */
export const weekdayStats = (reports: DayReport[]): WeekdayStat[] => {
    const buckets: DayReport[][] = [[], [], [], [], [], [], []];
    reports.filter(isActiveDay).forEach((d) => {
        buckets[dayjs(d.date).day()].push(d);
    });

    return buckets.map((list, weekday) => {
        const items = list.reduce((s, d) => s + d.items, 0);
        const acted = list.reduce((s, d) => s + d.acted, 0);
        return {
            weekday,
            label: WEEKDAY_LABEL[weekday],
            items,
            acted,
            rate: items ? acted / items : 0,
            activeDays: list.length,
        };
    });
};

/** 표본이 너무 적으면 요일 패턴이라고 말하면 안 된다 */
export const MIN_WEEKDAY_SAMPLE = 3;

export type WeekdayInsight = {
    worst: WeekdayStat | null;
    best: WeekdayStat | null;
    /** 최고와 최저 격차(%p) */
    gapPoint: number;
    /** 패턴이라고 부를 만한가 */
    meaningful: boolean;
    text: string;
};

export const weekdayInsight = (reports: DayReport[]): WeekdayInsight => {
    const stats = weekdayStats(reports).filter((w) => w.activeDays >= MIN_WEEKDAY_SAMPLE);
    if (stats.length < 2) {
        return {
            worst: null,
            best: null,
            gapPoint: 0,
            meaningful: false,
            text: '요일 패턴을 말하려면 기록이 더 쌓여야 합니다',
        };
    }
    const sorted = [...stats].sort((a, b) => a.rate - b.rate);
    const worst = sorted[0];
    const best = sorted[sorted.length - 1];
    const gapPoint = Math.round((best.rate - worst.rate) * 1000) / 10;
    // 20%p는 넘어야 우연이 아니라 습관이라고 볼 만하다
    const meaningful = gapPoint >= 20;

    return {
        worst,
        best,
        gapPoint,
        meaningful,
        text: meaningful
            ? `${worst.label}요일에 가장 많이 놓치고 있습니다 (${Math.round(worst.rate * 100)}%). ` +
              `가장 잘 챙기는 ${best.label}요일(${Math.round(best.rate * 100)}%)과 ${gapPoint}%p 차이입니다.`
            : '요일에 따른 차이는 크지 않습니다. 특정 요일 문제는 아닙니다.',
    };
};

/* ───────────────────────── 연속 기록 ───────────────────────── */

export type StreakInfo = {
    /** 최근 기록부터 이어지는 연속 일수 */
    current: number;
    /** 기간 내 최고 기록 */
    best: number;
    /** 연속이 끊긴 날 */
    brokenOn: string | null;
};

/**
 * 전부 실행한 날의 연속 기록.
 *
 * 이슈가 없던 날은 연속을 끊지 않는다. 챙길 게 없었을 뿐 잘못한 게 아니다.
 */
export const streakOf = (reports: DayReport[]): StreakInfo => {
    const sorted = [...reports].sort((a, b) => a.date.localeCompare(b.date));

    let best = 0;
    let run = 0;
    let current = 0;
    let brokenOn: string | null = null;

    sorted.forEach((d) => {
        if (!isActiveDay(d)) return;
        if (levelOf(d) === 'PERFECT') {
            run += 1;
            best = Math.max(best, run);
        } else {
            if (run > 0) brokenOn = d.date;
            run = 0;
        }
    });
    current = run;

    return { current, best, brokenOn };
};

/* ───────────────────────── 추세 ───────────────────────── */

export type TrendPoint = {
    weekStart: string;
    label: string;
    rate: number;
    items: number;
    defended: number | null;
};

/** 최근 N주 실행률 흐름 — 나아지는 중인지 나빠지는 중인지 */
export const recentTrend = (reports: DayReport[], weeks = 8): TrendPoint[] => {
    const buckets: Record<string, DayReport[]> = {};
    reports.filter(isActiveDay).forEach((d) => {
        const key = dayjs(d.date).startOf('week').format('YYYY-MM-DD');
        buckets[key] = [...(buckets[key] ?? []), d];
    });

    return Object.keys(buckets)
        .sort((a, b) => a.localeCompare(b))
        .slice(-weeks)
        .map((weekStart) => {
            const list = buckets[weekStart];
            const items = list.reduce((s, d) => s + d.items, 0);
            const acted = list.reduce((s, d) => s + d.acted, 0);
            return {
                weekStart,
                label: dayjs(weekStart).format('M/D'),
                rate: items ? acted / items : 0,
                items,
                defended: sumMoney(list, 'defended'),
            };
        });
};

/* ─────────────── 편성에서 빠진 항목 ─────────────── */

/**
 * 그날 브리핑에 **오르지 못한** 후보.
 *
 * 실행률은 "올라온 항목 중 몇 건을 했나"라서, 자리에 밀려 애초에 안 올라온 건은
 * 이 화면 어디에도 남지 않는다. 위험이 많은 날일수록 재등록 기회가 통째로 밀리는데
 * 화면은 "7건 중 3건 실행"으로만 보이니, 원장님은 그날 놓친 게 있는지 알 수 없다.
 *
 * 판단 기준은 오늘의 브리핑과 같은 곳(briefingPolicy.leftOutOf)을 쓴다.
 * 두 화면이 같은 날을 놓고 다른 숫자를 말하면 둘 다 못 믿게 된다.
 */
export const dayLeftOut = (d: DayReport | undefined): DayLeftOut | null =>
    leftOutOf(d?.composition);

export type CompositionCoverage = {
    monthKey: string;
    /** 편성 기록이 남아 있는 날 — 이 날들에 대해서만 무언가 말할 수 있다 */
    knownDays: number;
    /**
     * 브리핑은 올라왔는데 편성 기록이 없는 날 — **"모른다"** 인 날.
     * 0 건인 날과 절대 섞지 않는다. 섞는 순간 화면이 없는 사실을 말한다.
     */
    unknownDays: number;
    /** 자리가 없어 못 올라간 재등록 기회 후보 합 */
    chanceLeftOut: number;
    chanceLeftOutDays: number;
    /** 하루 절대 상한에 걸려 빠진 위험 후보 합 */
    dangerLeftOut: number;
    dangerLeftOutDays: number;
    /** 무언가 빠진 날 수 */
    leftOutDays: number;
    /** 화면에 그대로 쓸 한 줄. 아무 말도 할 게 없으면 빈 문자열 */
    text: string;
    /** 눈에 띄게 알려야 하는가 (빠진 것이 실제로 있었던 달) */
    alert: boolean;
};

/**
 * 한 달치 편성 기록 요약.
 *
 * `until` 이후(아직 오지 않은 날)는 세지 않는다. 미래는 편성 기록이 없는 게 당연한데
 * 그걸 "모르는 날"로 세면 8월 3일에 "28일은 알 수 없습니다"가 찍힌다.
 *
 * 서버는 브리핑 문서가 있는 날만 내려준다. 문서가 아예 없는 날은 편성 자체를
 * 하지 않은 날이라 "빠졌다"고 말할 대상이 아니므로 여기서도 세지 않는다.
 */
export const summarizeComposition = (
    reports: DayReport[],
    monthKey: string,
    until: string = dayjs().format('YYYY-MM-DD'),
): CompositionCoverage => {
    const rows = reports.filter((d) => monthKeyOf(d.date) === monthKey && d.date <= until);

    let knownDays = 0;
    let unknownDays = 0;
    let chanceLeftOut = 0;
    let chanceLeftOutDays = 0;
    let dangerLeftOut = 0;
    let dangerLeftOutDays = 0;
    let leftOutDays = 0;

    rows.forEach((d) => {
        const lo = dayLeftOut(d);
        // null 은 "모른다" — 여기서 0 으로 세면 안 된다
        if (!lo) {
            unknownDays += 1;
            return;
        }
        knownDays += 1;
        if (lo.chance > 0) {
            chanceLeftOut += lo.chance;
            chanceLeftOutDays += 1;
        }
        if (lo.danger > 0) {
            dangerLeftOut += lo.danger;
            dangerLeftOutDays += 1;
        }
        if (lo.total > 0) leftOutDays += 1;
    });

    const month = dayjs(`${monthKey}-01`).format('M월');
    const unknownTail = unknownDays > 0 ? ` · 편성 기록이 없는 ${unknownDays}일은 셈에서 뺐습니다` : '';

    let text = '';
    if (knownDays === 0 && unknownDays === 0) {
        // 브리핑 기록 자체가 없다. 이 화면은 애초에 빈 상태로 빠진다.
        text = '';
    } else if (knownDays === 0) {
        /*
         * 편성 기록이 하나도 없는 달.
         * 아무 말도 안 하면 "빠진 게 없었구나"로 읽힌다 — 8월에서 7월로 넘긴 원장님이
         * 7월을 깨끗한 달로 오해한다. 모르는 것은 모른다고 적는다.
         */
        text = `${month}은 편성 기록이 남아 있지 않아, 브리핑에 오르지 못한 후보가 있었는지는 알 수 없습니다`;
    } else if (leftOutDays === 0) {
        text = `${month}은 판정을 통과한 후보가 모두 브리핑에 올랐습니다 (편성 기록이 있는 ${knownDays}일 기준)${unknownTail}`;
    } else {
        const parts: string[] = [];
        if (chanceLeftOut > 0)
            parts.push(
                `재등록 기회 후보 ${chanceLeftOut}건이 자리가 없어 ${month} 브리핑에 오르지 못했습니다 (${chanceLeftOutDays}일)`,
            );
        if (dangerLeftOut > 0)
            parts.push(
                `위험 후보 ${dangerLeftOut}건이 하루 절대 상한에 걸려 ${month} 브리핑에서 빠졌습니다 (${dangerLeftOutDays}일)`,
            );
        text = parts.join(' · ') + unknownTail;
    }

    return {
        monthKey,
        knownDays,
        unknownDays,
        chanceLeftOut,
        chanceLeftOutDays,
        dangerLeftOut,
        dangerLeftOutDays,
        leftOutDays,
        text,
        alert: leftOutDays > 0,
    };
};

/**
 * 탭에 들어왔을 때 **먼저 열어 둘 날짜.**
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 자동으로 고르나
 * ─────────────────────────────────────────────────────────────
 * 예전에는 아무 날도 안 골라 둔 채로 열렸다. 히트맵만 덩그러니 있고,
 * 원장님이 달력에서 칸 하나를 찾아 눌러야 그날 상세가 처음 나왔다.
 * 매일 아침 쓰는 화면에서 "무엇을 눌러야 하는지"를 매번 찾게 만들면
 * 대부분은 안 누르고 나간다.
 *
 * 오늘이 가장 자주 보고 싶은 날이므로 오늘을 연다. 오늘 기록이 없으면
 * (아직 생성 전이거나 만들어지지 않은 날) **가장 최근 기록일**을 연다.
 *
 * ─────────────────────────────────────────────────────────────
 * 고를 수 있는 날의 기준은 달력 칸과 **같아야 한다**
 * ─────────────────────────────────────────────────────────────
 * 히트맵은 `items > 0` 이거나 브리핑에 못 오른 후보가 있는 날만 눌리게 한다
 * (OpsBriefingHistoryPage 의 `openable`). 여기서 그보다 넓게 고르면
 * 화면은 상세를 열어 두는데 달력에서는 그 칸이 안 눌리는, 서로 어긋난
 * 상태가 된다. 그래서 같은 판정을 쓴다.
 *
 * 미래 날짜는 고르지 않는다. 아직 오지 않은 날을 열어 두면
 * "그날은 브리핑이 없다"고 말하게 된다.
 */
export const defaultHistoryDate = (
    reports: DayReport[],
    todayKey: string
): string | null => {
    const openable = (d: DayReport) => d.items > 0 || (dayLeftOut(d)?.total ?? 0) > 0;
    const usable = reports.filter((d) => d.date <= todayKey && openable(d));
    if (usable.length === 0) return null;
    /* 오늘 기록이 있으면 오늘. 없으면 가장 최근 기록일 */
    const today = usable.find((d) => d.date === todayKey);
    if (today) return today.date;
    return usable.reduce((a, b) => (a.date >= b.date ? a : b)).date;
};

/** 기록이 있는 가장 최근 달 — 빈 달을 먼저 보여주면 화면이 죽는다 */
export const latestMonthWithData = (reports: DayReport[], fallback: string): string => {
    const keys = reports
        .filter(isActiveDay)
        .map((d) => monthKeyOf(d.date))
        .filter((k, i, arr) => arr.indexOf(k) === i)
        .sort();
    return keys[keys.length - 1] ?? fallback;
};
